// src/pages/StaffDashboard.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "../api/axios";
import "./StaffDashboard.css";

import GroupedApplicationsList from "./GroupedApplicationsList";
import InExpertiseApplicationsList from "./InExpertiseApplicationsList";
import GroupedApplicationsDeputy2 from "./GroupedApplicationsDeputy2";
import GroupedApplicationsDepartmentHead from "./GroupedApplicationsDepartmentHead";
import GroupedApplicationsInternalExpert from "./GroupedApplicationsInternalExpert";
import GroupedApplicationsExternalExpert from "./GroupedApplicationsExternalExpert";
import SelectCriteriaInternalExpert from "./SelectCriteriaInternalExpert";
import ExternalExpertApplicationsList from "./ExternalExpertApplicationsList";
import DocumentsPage from "./DocumentsPage";
import InExpertiseApplicationsInternalExpert from "./InExpertiseApplicationsInternalExpert";
import InExpertiseApplicationsZam2 from "../pages/InExpertiseApplicationsZam2";
import UsersPage from "./UsersPage";
import SummaryPage from "./SummaryPage";
import PublishersPage from "./PublishersPage";
import GovInboxTab from "./GovInboxTab";
import StaffInboxTab from "./StaffInboxTab";
import DirectorInboxTab from "./DirectorInboxTab";
import InternalExpertHub from './InternalExpertHub';

import ModeratorSubjectsPage from "./ModeratorSubjectsPage";
import ModeratorCriteriaCategoriesPage from "./ModeratorCriteriaCategoriesPage";
import ModeratorCriteriasPage from "./ModeratorCriteriasPage";
import ReferencesPage from "./ReferencesPage";

import StaffProfileTab from "./StaffProfileTab";

import OrderList from "./OrderList";
import NewOrderCreate from "./NewOrderCreate";
// FIX: убран дублирующий импорт — объединено в один
import { FaHome, FaUser, FaSignOutAlt, FaUsers, FaFileSignature } from "react-icons/fa";

// ─────────────────────────────────────────────────────────────────────────────
// ROLE_TABS_CONFIG — маппинг роль → компонент для вкладки "Новые заявки"
// departmentId === 6 → GovInboxTab, остальные department_supervisor → GroupedApplicationsDepartmentHead
// ─────────────────────────────────────────────────────────────────────────────
const buildRoleTabsConfig = (locale, departmentId) => [
  {
    key: "department_supervisor",
    labelKz: "Бөлім басшысы",
    labelRu: "Руководитель отдела",
    roles: ["department_supervisor", "бөлім басшысы"],
    renderContent: () =>
      departmentId === 6 ? (
        <GovInboxTab locale={locale} isHead={true} activeRole="department_supervisor" />
      ) : (
        <GroupedApplicationsDepartmentHead locale={locale} />
      ),
  },
  {
    key: "staff",
    labelKz: "Қызметкер",
    labelRu: "Сотрудник",
    roles: ["staff", "сотрудник"],
    renderContent: () => <StaffInboxTab locale={locale} activeRole="staff" />,
  },
  {
    key: "director",
    labelKz: "Директор",
    labelRu: "Директор",
    roles: ["director", "директор"],
    renderContent: () => <DirectorInboxTab locale={locale} />,
  },
  {
    key: "deputy_director_2",
    labelKz: "Директор орынбасары 2",
    labelRu: "Зам. директора 2",
    roles: ["deputy_director_2", "директор орынбасары 2", "зам директор 2"],
    renderContent: () => <GroupedApplicationsDeputy2 locale={locale} />,
  },
{
  key: 'internal_expert',
  labelKz: 'Ішкі сарапшы',
  labelRu: 'Внутренний эксперт',
  roles: ['internal_expert', 'ішкі сарапшы', 'internal expert'],
  // Вместо GroupedApplicationsInternalExpert — наш хаб
  renderContent: () => <InternalExpertHub locale={locale} />,
},
  {
    key: "external_expert",
    labelKz: "Сыртқы сарапшы",
    labelRu: "Внешний эксперт",
    roles: ["external_expert", "сыртқы сарапшы", "external expert"],
    renderContent: () => <GroupedApplicationsExternalExpert locale={locale} />,
  },
  {
    key: "default",
    labelKz: "Қызметкер",
    labelRu: "Сотрудник",
    roles: [],
    renderContent: () => <GroupedApplicationsList locale={locale} />,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// NewApplicationsWithRoleTabs
// 1 роль → без вкладок, просто контент
// 2+ роли → вкладки сверху
// ─────────────────────────────────────────────────────────────────────────────
function NewApplicationsWithRoleTabs({ locale, rolesLower, departmentId }) {
  const config = useMemo(
    () => buildRoleTabsConfig(locale, departmentId),
    [locale, departmentId]
  );

  const tabs = useMemo(() => {
    const matched = config.filter(
      (cfg) =>
        cfg.roles.length > 0 &&
        cfg.roles.some((r) => rolesLower.some((ur) => ur === r))
    );
    if (matched.length === 0) {
      const fallback = config.find((c) => c.key === "default");
      return fallback ? [fallback] : [];
    }
    return matched;
  }, [config, rolesLower]);

  const [activeKey, setActiveKey] = useState(() => tabs[0]?.key || "");

  useEffect(() => {
    if (tabs.length && !tabs.find((t) => t.key === activeKey)) {
      setActiveKey(tabs[0].key);
    }
  }, [tabs, activeKey]);

  const currentTab = tabs.find((t) => t.key === activeKey) || tabs[0];

  return (
    <section className="dashboard-body">
      {tabs.length > 1 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            background: "#e2e8f0",
            borderRadius: 8,
            padding: 4,
            marginBottom: 20,
            width: "fit-content",
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeKey === tab.key;
            const label = locale === "kz" ? tab.labelKz : tab.labelRu;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveKey(tab.key)}
                style={{
                  padding: "8px 20px",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 13,
                  background: isActive ? "#fff" : "transparent",
                  color: isActive ? "#0f172a" : "#64748b",
                  boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div>{currentTab?.renderContent()}</div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FIX: Видеоинструкции вынесены за пределы компонента — не пересоздаются при рендере
// ─────────────────────────────────────────────────────────────────────────────
const DEPT_HEAD_VIDEOS = {
  kz: [
    { title: "Нұсқаулық 1", url: "https://drive.google.com/file/d/1biBlKPn_lqRiEUjr2Iyma71oiLke5kww/view?usp=sharing" },
    { title: "Нұсқаулық 2", url: "https://drive.google.com/file/d/1UV3DrVF5I5PIbY-7Eh6pCHaZxMSNUEEI/view?usp=sharing" },
    { title: "Нұсқаулық 3", url: "https://drive.google.com/file/d/1SOTCfFrCuXJNSwB4yb8euzciI4S7RUt_/view?usp=sharing" },
    { title: "Нұсқаулық 4", url: "https://drive.google.com/file/d/1YCCbvz5RqggKdG83HkF-yjIiUuLdEyQn/view?usp=sharing" },
  ],
  ru: [
    { title: "Инструкция 1", url: "https://drive.google.com/file/d/1qnHMFZuGi_mRxmEsZd0Q-mPXiuNz2vYj/view?usp=sharing" },
    { title: "Инструкция 2", url: "https://drive.google.com/file/d/1U3bf0Xt4MfnJhx6OQXbR8t6KOKJatKND/view?usp=sharing" },
  ],
};

const DIRECTOR_VIDEOS = {
  kz: [
    { title: "Нұсқаулық 1", url: "https://drive.google.com/file/d/1u2gNrAjcnURkPhl7unHvsWdoEXf3qg-p/view?usp=sharing" },
    { title: "Нұсқаулық 2", url: "https://drive.google.com/file/d/11gTKrH-ADsxQI9pqdSwa2qtzwlaZ56d9/view?usp=sharing" },
    { title: "Нұсқаулық 3", url: "https://drive.google.com/file/d/1d0QKedzJd2yM1PC5x3MSl5is9PJ-mbVI/view?usp=sharing" },
    { title: "Нұсқаулық 4", url: "https://drive.google.com/file/d/1ov2La-ucbvNfzIOqo_6H2-rAwmqTp-pC/view?usp=sharing" },
  ],
  ru: [
    { title: "Инструкция 1", url: "https://drive.google.com/file/d/1N5iewjnkZUX2Q19pX2jtm6HS_OwzlMYi/view?usp=sharing" },
    { title: "Инструкция 2", url: "https://drive.google.com/file/d/1Xms55FMg-RBzkn2ujCMeUTbyJQs5g9_l/view?usp=sharing" },
  ],
};

const DEPUTY_DIRECTOR_VIDEOS = {
  kz: [
    { title: "Нұсқаулық 1", url: "https://drive.google.com/file/d/1gJ7dS2ZUKelCa72nE56vLRdXA3xTHivE/view?usp=sharing" },
    { title: "Нұсқаулық 2", url: "https://drive.google.com/file/d/1DvMZuUjVdHj0a6XGSoDwbv1Vkxjk8ENR/view?usp=sharing" },
    { title: "Нұсқаулық 3", url: "https://drive.google.com/file/d/1CWssDtzFk-IQ_HmPEsPI1866hHd1Uuky/view?usp=sharing" },
    { title: "Нұсқаулық 4", url: "https://drive.google.com/file/d/1yNPNe8PGubNmg0zQL3PFS1_0-ojDTQFt/view?usp=sharing" },
  ],
  ru: [
    { title: "Инструкция 1", url: "https://drive.google.com/file/d/1iMuNygHZkBcvv1Q7b5R8ktxWbyq3AZrI/view?usp=sharing" },
    { title: "Инструкция 2", url: "https://drive.google.com/file/d/13rX-we_hrIAAOHNb6cWEO6czepaGGSkB/view?usp=sharing" },
    { title: "Инструкция 3", url: "https://drive.google.com/file/d/1VYgmef3rePb_Hgcm20hwNMvLwFmJ0Qg-/view?usp=sharing" },
  ],
};

const ACCOUNTANT_VIDEOS = {
  kz: [
    { title: "Нұсқаулық 1", url: "https://drive.google.com/file/d/16BmQfE64-doyMvF6pxrQt8FrfccQXkrG/view?usp=sharing" },
    { title: "Нұсқаулық 2", url: "https://drive.google.com/file/d/1uxRai6lb09gbkLiNlluwT2MtdbRHeNOG/view?usp=sharing" },
    { title: "Нұсқаулық 3", url: "https://drive.google.com/file/d/1AsvpzbcLKUvEN4Q6iDAgWk03nkqdLoLg/view?usp=sharing" },
  ],
  ru: [
    { title: "Инструкция 1", url: "https://drive.google.com/file/d/1esEV9IMdYtjQ16tBHOh4wwtfv6n3NRR4/view?usp=sharing" },
    { title: "Инструкция 2", url: "https://drive.google.com/file/d/1lS9DxvirzIteV7mvpymvdc9MA9sQLdun/view?usp=sharing" },
    { title: "Инструкция 3", url: "https://drive.google.com/file/d/15RqOtCbbqknmYaDOZYk84Qcd6abc9YsH/view?usp=sharing" },
    { title: "Инструкция 4", url: "https://drive.google.com/file/d/1AcMyfssw3ZaLuzAJaKqz9xom2NG0ZKFn/view?usp=sharing" },
  ],
};

const INTERNAL_EXPERT_VIDEOS = {
  kz: [
    { title: "Нұсқаулық 1", url: "https://drive.google.com/file/d/1VkZmQq2e0BtVD2Z2ZdJKU9wl-0_LwKWd/view?usp=sharing" },
    { title: "Нұсқаулық 2", url: "https://drive.google.com/file/d/1FrYyLyPn4gpxtLSaXdlt-2u89cZIj4AN/view?usp=sharing" },
    { title: "Нұсқаулық 3", url: "https://drive.google.com/file/d/1XkfXs6vPAEtLyB6i8h7y69PmvGv_wVpv/view?usp=sharing" },
    { title: "Нұсқаулық 4", url: "https://drive.google.com/file/d/1UNTaMGYN_jd4O2NfWIN0i_RivY_ncFJP/view?usp=sharing" },
    { title: "Нұсқаулық 5", url: "https://drive.google.com/file/d/1jhhXY0FmsceZH8ZshuSUj4p9_mUQlaAo/view?usp=sharing" },
    { title: "Нұсқаулық 6", url: "https://drive.google.com/file/d/1I6iEsKcHRmbsAYZnyknBmznsFprLnhgP/view?usp=sharing" },
    { title: "Нұсқаулық 7", url: "https://drive.google.com/file/d/13GX325xJwFC3T5rov-AN9wquKhWHFHBr/view?usp=sharing" },
    { title: "Нұсқаулық 8", url: "https://drive.google.com/file/d/19Y0UzgE7zfELA5G_MkQKj0yyVDlFftZW/view?usp=sharing" },
    { title: "Нұсқаулық 9", url: "https://drive.google.com/file/d/1Cu8O4N-akhHBhNXoVXPV5YS9-m3wLs66/view?usp=sharing" },
    { title: "Нұсқаулық 10", url: "https://drive.google.com/file/d/1cWhO__uwb_vsJ4Cb8-5rlwVjTTu93E7o/view?usp=sharing" },
  ],
  ru: [
    { title: "Инструкция 1", url: "https://drive.google.com/file/d/1siPGRMWzdKe-BrFxxFTZdP-as231-1CA/view?usp=sharing" },
    { title: "Инструкция 2", url: "https://drive.google.com/file/d/1YdXqTHkxwdu00HMJbNgHvQjwKqobgk_h/view?usp=sharing" },
    { title: "Инструкция 3", url: "https://drive.google.com/file/d/1ws-xtoZFm8qQ5vD91O3Z-sEM2p0yr078/view?usp=sharing" },
    { title: "Инструкция 4", url: "https://drive.google.com/file/d/1faveApX5NGGhrR3vfq0AD-AMWknFu2x9/view?usp=sharing" },
    { title: "Инструкция 5", url: "https://drive.google.com/file/d/1PRvVVe_Au5-nrYPofv2QQb01trRfr3b2/view?usp=sharing" },
    { title: "Инструкция 6", url: "https://drive.google.com/file/d/1jnHMIidAg9KS7f9dG1T8ztuZrGuAkGH1/view?usp=sharing" },
    { title: "Инструкция 7", url: "https://drive.google.com/file/d/1Y4Uz5wbkesrpKBzrSoneamw6GF_KYTsf/view?usp=sharing" },
    { title: "Инструкция 8", url: "https://drive.google.com/file/d/1YcxJqLqxQVynOOjhQqXORR1XgIYT_58j/view?usp=sharing" },
  ],
};

const EXTERNAL_EXPERT_VIDEOS = {
  kz: [
    { title: "Нұсқаулық 1", url: "https://drive.google.com/file/d/1zv_HbVl4vRHJ_0mv7ifb_A_gKdqhx--R/view?usp=sharing" },
    { title: "Нұсқаулық 2", url: "https://drive.google.com/file/d/1caXPGMkGuEq2ilBxhK6olBZg6j9IQqRi/view?usp=sharing" },
    { title: "Нұсқаулық 3", url: "https://drive.google.com/file/d/1PkES2PH5MGoa87Jv26SwqA5Zs_xAdGnY/view?usp=sharing" },
    { title: "Нұсқаулық 4", url: "https://drive.google.com/file/d/19Rm0ZKK2862N9kT-Hge_w8UT7ZWPOibG/view?usp=sharing" },
    { title: "Нұсқаулық 5", url: "https://drive.google.com/file/d/1lTcALv5vmxe7rCSQ-ve5fHNr7OwKybLl/view?usp=sharing" },
  ],
  ru: [
    { title: "Инструкция 1", url: "https://drive.google.com/file/d/1sI2inrvLZidAguf3ejxD8r4vBsb40ake/view?usp=sharing" },
    { title: "Инструкция 2", url: "https://drive.google.com/file/d/1EJ4JJzA-07F1hQj1yarAUpocdCyeQlFK/view?usp=sharing" },
    { title: "Инструкция 3", url: "https://drive.google.com/file/d/1Gi3Txbuxi3OEDtgYdS8oocDDhJS14U1Z/view?usp=sharing" },
    { title: "Инструкция 4", url: "https://drive.google.com/file/d/1Rm9q8P5D5qIqNAy6hIWN_1P5nh9Rf2t8/view?usp=sharing" },
    { title: "Инструкция 5", url: "https://drive.google.com/file/d/1UkmwM1ErIhXdMyJQU7E-7Z2PYgafbMm8/view?usp=sharing" },
    { title: "Инструкция 6", url: "https://drive.google.com/file/d/13OM32cfv9MCwotzNKzeSpZ835HnidfSm/view?usp=sharing" },
  ],
};

const REGISTRAR_VIDEOS = {
  kz: [
    { title: "Нұсқаулық 1", url: "https://drive.google.com/file/d/1gaFA-oarzpk_OE8cmN4OR34eYP0jrMN8/view?usp=sharing" },
    { title: "Нұсқаулық 2", url: "https://drive.google.com/file/d/103SmsBEG0jJFGtkWrfPfQTrWGdCScqbo/view?usp=sharing" },
    { title: "Нұсқаулық 3", url: "https://drive.google.com/file/d/1_mKrLCmKbAUcCXez2sArr09GtnXPz09E/view?usp=sharing" },
    { title: "Нұсқаулық 4", url: "https://drive.google.com/file/d/10VUkxW-2hvaWFsVv-jt65mwtgqdOVYXq/view?usp=sharing" },
    { title: "Нұсқаулық 5", url: "https://drive.google.com/file/d/1Ec1Dkvohr31dDazZsAOwiY7_JqMxivd7/view?usp=sharing" },
    { title: "Нұсқаулық 6", url: "https://drive.google.com/file/d/1i9hE4FG6269yc-LLm-NHOoRxcYAxHDMb/view?usp=sharing" },
    { title: "Нұсқаулық 7", url: "https://drive.google.com/file/d/1pj-mTIFAb_NfNycO8h5rSC0tbUAJuXdS/view?usp=sharing" },
  ],
  ru: [
    { title: "Инструкция 1", url: "https://drive.google.com/file/d/1Ql2mTQH4IqMGGTcyz96KO-7tCuq9OIXe/view?usp=sharing" },
    { title: "Инструкция 2", url: "https://drive.google.com/file/d/19a-V6yZyHqZyF-_FQBe1IziNw6WK0Now/view?usp=sharing" },
    { title: "Инструкция 3", url: "https://drive.google.com/file/d/1adWezLivptQSo7qX4pT84zv47t6NjVKq/view?usp=sharing" },
    { title: "Инструкция 4", url: "https://drive.google.com/file/d/1Uvq_PTM5YgYdQQ_mIwcU98oZhxt2Kfb8/view?usp=sharing" },
    { title: "Инструкция 5", url: "https://drive.google.com/file/d/1FkvLNcMDtJBR0pzEcIftJ3ZNtqKNkFFl/view?usp=sharing" },
    { title: "Инструкция 6", url: "https://drive.google.com/file/d/1zh_VX-PHOJpaElHsDxAKOoxB37TwvuuW/view?usp=sharing" },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
export default function StaffDashboard() {
  const [locale, setLocale] = useState(localStorage.getItem("locale") || "kz");

  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("home");
  const [critHubTab, setCritHubTab] = useState("categories");

  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState("");

  // ===== Bootstrap SSO directly inside dashboard =====
  useEffect(() => {
    let cancelled = false;

    const bootstrapSso = async () => {
      const params = new URLSearchParams(window.location.search);
      const ssoKey = params.get("sso_key");
      const existingToken = localStorage.getItem("token");

      const hasValidExistingToken =
        existingToken && existingToken !== "null" && existingToken !== "undefined";

      if (ssoKey) {
        try {
          const res = await axios.post("/auth/idp/exchange", { key: ssoKey });

          const token = res?.data?.token;
          const user = res?.data?.user;

          if (!token) {
            throw new Error("Token not returned");
          }

          localStorage.setItem("token", token);
          localStorage.setItem("user_id", user?.id ?? "");
          localStorage.setItem("username", user?.name ?? "");
          localStorage.setItem("role", user?.role || "");
          localStorage.setItem("department", user?.department || "");

          params.delete("sso_key");
          params.delete("sso");

          const cleanUrl =
            `${window.location.pathname}` +
            `${params.toString() ? `?${params.toString()}` : ""}` +
            `${window.location.hash}`;

          window.history.replaceState({}, document.title, cleanUrl);

          if (!cancelled) {
            setAuthReady(true);
            setAuthError("");
          }
          return;
        } catch (err) {
          localStorage.removeItem("token");
          localStorage.removeItem("user_id");
          localStorage.removeItem("username");
          localStorage.removeItem("role");
          localStorage.removeItem("department");

          const msg = err?.response?.data?.message || err?.message || "SSO exchange failed";

          if (!cancelled) {
            setAuthError(msg);
            setAuthReady(false);
          }

          window.location.href = `/login?sso_error=${encodeURIComponent(msg)}`;
          return;
        }
      }

      if (hasValidExistingToken) {
        if (!cancelled) {
          setAuthReady(true);
          setAuthError("");
        }
        return;
      }

      window.location.href = "/login";
    };

    bootstrapSso();

    return () => {
      cancelled = true;
    };
  }, []);

  // =========================================================
  // ROLE NORMALIZATION
  // =========================================================
  const rolesLower = useMemo(() => {
    const fromArray = Array.isArray(profile?.roles) ? profile.roles : [];
    const fromOld = profile?.role?.name ? [profile.role.name] : [];

    return [...fromArray, ...fromOld]
      .map((x) => String(x || "").toLowerCase().trim())
      .filter(Boolean);
  }, [profile]);

  const roleNameLower = useMemo(() => rolesLower.join(" | "), [rolesLower]);

  const hasRole = useCallback(
    (...needles) => {
      const n = needles
        .flat()
        .map((x) => String(x || "").toLowerCase().trim())
        .filter(Boolean);

      if (!n.length) return false;

      return rolesLower.some((r) =>
        n.some((needle) => r === needle || r.includes(needle))
      );
    },
    [rolesLower]
  );

  // =========================================================
  // FLAGS
  // FIX: isDeputy2 теперь проверяет точное совпадение по ключевым ролям,
  //      чтобы директор с подстрокой "директор" в имени не стал isDeputy2.
  //      isDirector проверяется через точное совпадение "director" / "директор",
  //      и дополнительно исключает isDeputy2 по точному ключу роли.
  // =========================================================
  const isGosService = profile?.department?.id === 6;
  const isModerator = hasRole("moderator", "модератор");
  const isRegistrar = hasRole("moderator_registrar", "registrar");
  const isAccountant = hasRole("moderator_accountant", "accountant", "бухгалтер");

  const isExternalExpert = hasRole("external_expert", "сыртқы сарапшы", "external expert");
  const isInternalExpert = hasRole("internal_expert", "ішкі сарапшы", "internal expert");

  // FIX: isDeputy2 — проверяем только точные роли, не подстроку
  const isDeputy2 = useMemo(
    () =>
      rolesLower.some((r) =>
        ["deputy_director_2", "директор орынбасары 2", "зам директор 2"].includes(r)
      ),
    [rolesLower]
  );

  const isDepartmentHead = hasRole("department_supervisor", "бөлім басшысы");

  // FIX: isDirector — точное совпадение, независимо от isDeputy2
  const isDirector = useMemo(
    () =>
      !isDeputy2 &&
      rolesLower.some((r) => r === "director" || r === "директор"),
    [rolesLower, isDeputy2]
  );

  const canOpenUsers =
    (rolesLower.some((r) =>
      ["директор", "director", "admin", "админ", "administrator", "moderator"].includes(r)
    )) && !isDeputy2;

  const canOpenPublishers = canOpenUsers;

  // ===== Dictionaries =====
  const roleNames = useMemo(
    () => ({
      1: { kz: "Модератор", ru: "Модератор" },
      2: { kz: "Сотрудник", ru: "Сотрудник" },
      3: { kz: "Ішкі сарапшы", ru: "internal_expert" },
      4: { kz: "Бөлім басшысы", ru: "department_supervisor" },
      5: { kz: "Маман", ru: "Специалист" },
      6: { kz: "Директор орынбасары 2", ru: "deputy_director_2" },
      7: { kz: "Директор орынбасары 1", ru: "deputy_director_1" },
      8: { kz: "Директор", ru: "Директор" },
      9: { kz: "Ғылыми хатшы", ru: "Учёный секретарь" },
      10: { kz: "Сыртқы сарапшы", ru: "external_expert" },
      11: { kz: "Архивариус", ru: "Архивариус" },
    }),
    []
  );

  const departmentNames = useMemo(
    () => ({
      6: { kz: "Мемлекеттік қызметтер бөлімі", ru: "Отдел гос. услуг" },
    }),
    []
  );

  // FIX: ID отдела, скрывающего вкладку inExpertise, вынесен в константу —
  //      не зависит от точной строки названия отдела из БД
  const DEPT_ID_HIDE_IN_EXPERTISE = 6;

  // ===== Helpers =====
  // FIX: handleTabChange проверяет права перед переключением вкладки
  const handleTabChange = useCallback(
    (tab) => {
      // Защита: нельзя перейти на users/publishers без прав
      if ((tab === "users" || tab === "publishers") && !canOpenUsers) return;

      const url = `/staff-groups?tab=${tab}#${tab}`;
      sessionStorage.setItem("ee_last_list", url);
      sessionStorage.setItem("staff_groups_active_tab", tab);
      localStorage.setItem("staff_groups_active_tab", tab);
      setActiveTab(tab);
    },
    [canOpenUsers]
  );

  const toggleLanguage = useCallback(
    (nextLang) => {
      const next = nextLang ?? (locale === "kz" ? "ru" : "kz");
      localStorage.setItem("locale", next);
      axios.defaults.headers.common["Accept-Language"] = next === "kz" ? "kk" : "ru";
      setLocale(next);
      // FIX: langVersion убран — locale передаётся как prop, перемонтирование не нужно
    },
    [locale]
  );

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    localStorage.removeItem("department");
    window.location.href = "/login";
  };

  // ===== URL sync on tab =====
  useEffect(() => {
    const url = `/staff-groups?tab=${activeTab}#${activeTab}`;
    window.history.replaceState(null, "", url);
    sessionStorage.setItem("ee_last_list", url);
  }, [activeTab]);

  // ===== Set axios language header =====
  useEffect(() => {
    axios.defaults.headers.common["Accept-Language"] = locale === "kz" ? "kk" : "ru";
  }, [locale]);

  // ===== Restore tab on boot =====
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params.get("tab");

    const bootTab =
      tabFromUrl ||
      sessionStorage.getItem("staff_groups_active_tab") ||
      localStorage.getItem("staff_groups_active_tab");

    if (bootTab) setActiveTab(bootTab);
  }, []);

  // ===== Load profile only after auth ready =====
  // FIX: AbortController предотвращает setState после unmount / гонку запросов
  useEffect(() => {
    if (!authReady) return;

    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") {
      window.location.href = "/login";
      return;
    }

    const controller = new AbortController();

    axios
      .get("/internal/profile", {
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": locale === "kz" ? "kk" : "ru",
        },
      })
      .then((res) => setProfile(res.data))
      .catch((err) => {
        if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
        localStorage.removeItem("token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("username");
        localStorage.removeItem("role");
        localStorage.removeItem("department");
        window.location.href = "/login";
      });

    return () => controller.abort();
  }, [locale, authReady]);

  // ===== Instruction video helpers =====
  const getDrivePreviewUrl = useCallback((url) => {
    const m = String(url || "").match(/\/file\/d\/([^/]+)/);
    const id = m?.[1];
    return id ? `https://drive.google.com/file/d/${id}/preview` : url;
  }, []);

  // FIX: pickInstructionVideos теперь зависит только от hasRole —
  //      видеоконстанты вынесены за компонент и стабильны
  const pickInstructionVideos = useCallback(() => {
    if (hasRole("moderator_registrar", "registrar")) {
      return { titleKz: "moderator_registrar үшін нұсқаулық", titleRu: "Инструкция для moderator_registrarа", ...REGISTRAR_VIDEOS };
    }
    if (hasRole("external_expert", "сыртқы сарапшы", "external expert")) {
      return { titleKz: "Сыртқы сарапшы үшін нұсқаулық", titleRu: "Инструкция для внешнего эксперта", ...EXTERNAL_EXPERT_VIDEOS };
    }
    if (hasRole("internal_expert", "ішкі сарапшы", "internal expert")) {
      return { titleKz: "Ішкі сарапшы үшін нұсқаулық", titleRu: "Инструкция для внутреннего эксперта", ...INTERNAL_EXPERT_VIDEOS };
    }
    if (hasRole("moderator_accountant", "accountant", "бухгалтер")) {
      return { titleKz: "Бухгалтер үшін нұсқаулық", titleRu: "Инструкция для бухгалтера", ...ACCOUNTANT_VIDEOS };
    }
    if (hasRole("deputy_director_2", "директор орынбасары", "заместитель директора", "зам директор")) {
      return { titleKz: "Директор орынбасары үшін нұсқаулық", titleRu: "Инструкция для заместителя директора", ...DEPUTY_DIRECTOR_VIDEOS };
    }
    if (hasRole("director", "директор")) {
      return { titleKz: "Директор үшін нұсқаулық", titleRu: "Инструкция для директора", ...DIRECTOR_VIDEOS };
    }
    if (hasRole("department_supervisor", "бөлім басшысы")) {
      return { titleKz: "Бөлім басшысы үшін нұсқаулық", titleRu: "Инструкция для руководителя отдела", ...DEPT_HEAD_VIDEOS };
    }
    return { titleKz: "Нұсқаулық", titleRu: "Инструкция", kz: [], ru: [] };
  }, [hasRole]);

  // =========================================================
  // RENDER CONTENT
  // =========================================================
  const renderContent = () => {
    // FIX: добавлена ветка "home" с приветственным контентом
    if (activeTab === "home") {
      return (
        <section className="dashboard-body">
          <div className="pd-card">
            <p style={{ color: "var(--color-text-secondary)", marginTop: 8 }}>
              {locale === "kz"
                ? "Жұмыс кеңістігіне қош келдіңіз. Сол жақтағы мәзірден бөлімді таңдаңыз."
                : "Добро пожаловать в рабочее пространство. Выберите раздел в меню слева."}
            </p>
          </div>
        </section>
      );
    }

    if (activeTab === "references") return <ReferencesPage locale={locale} />;

    if (activeTab === "publishers") return <PublishersPage locale={locale} />;

    if (activeTab === "orders") return <OrderList locale={locale} />;
    if (activeTab === "newOrder") return <NewOrderCreate locale={locale} />;

    if (activeTab === "instruction") {
      const picked = pickInstructionVideos();
      const title = locale === "kz" ? picked.titleKz : picked.titleRu;
      const list = (locale === "kz" ? picked.kz : picked.ru) || [];

      if (!list.length) {
        return (
          <section className="dashboard-body">
            <div className="pd-card pd-card--video">
              <h2 className="pd-video-title">{title}</h2>
              <p className="pd-muted" style={{ marginTop: 10 }}>
                {locale === "kz"
                  ? "Сіздің рөліңіз үшін нұсқаулық әзірге қосылмаған."
                  : "Для вашей роли инструкция пока не добавлена."}
              </p>
              {!!roleNameLower && (
                <p className="pd-muted" style={{ marginTop: 6 }}>
                  role: <b>{roleNameLower}</b>
                </p>
              )}
            </div>
          </section>
        );
      }

      return (
        <section className="dashboard-body">
          <div className="pd-card pd-card--video">
            <h2 className="pd-video-title" style={{ marginTop: 0 }}>
              {title}
            </h2>
            <div style={{ display: "grid", gap: 16, marginTop: 12 }}>
              {list.map((v, idx) => {
                const previewUrl = getDrivePreviewUrl(v.url);
                return (
                  <div key={idx} className="pd-video-block">
                    <div className="pd-video-head">
                      <h3 className="pd-video-title" style={{ margin: 0 }}>
                        {v.title}
                      </h3>
                      <a className="pd-link" href={v.url} target="_blank" rel="noreferrer">
                        {locale === "kz" ? "Drive-та ашу" : "Открыть в Drive"}
                      </a>
                    </div>
                    <div className="pd-video-wrap">
                      <iframe
                        title={`role-instruction-video-${idx}`}
                        src={previewUrl}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="pd-muted" style={{ marginTop: 12 }}>
              {locale === "kz"
                ? "Видео сіздің рөліңізге және тілге сәйкес көрсетіледі."
                : "Видео показывается в зависимости от роли и языка."}
            </p>
          </div>
        </section>
      );
    }

    if (activeTab === "moderatorSubjects") return <ModeratorSubjectsPage locale={locale} />;

    if (activeTab === "moderatorCriteriasHub") {
      return (
        <section className="dashboard-body">
          <div className="pd-card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className={`pd-btn pd-btn--sm ${critHubTab === "categories" ? "" : "pd-btn--ghost"}`}
                  onClick={() => setCritHubTab("categories")}
                >
                  🗂️ {locale === "kz" ? "Категориялар" : "Категории"}
                </button>
                <button
                  type="button"
                  className={`pd-btn pd-btn--sm ${critHubTab === "criterias" ? "" : "pd-btn--ghost"}`}
                  onClick={() => setCritHubTab("criterias")}
                >
                  🧮 {locale === "kz" ? "Критерийлер" : "Критерии"}
                </button>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              {critHubTab === "categories" ? (
                <ModeratorCriteriaCategoriesPage locale={locale} />
              ) : (
                <ModeratorCriteriasPage locale={locale} />
              )}
            </div>
          </div>
        </section>
      );
    }

    if (activeTab === "profile") {
      return (
        <StaffProfileTab
          locale={locale}
          profile={profile}
          rolesLower={rolesLower}
          roleNames={roleNames}
          departmentNames={departmentNames}
          onProfilePatch={(patch) => setProfile((prev) => ({ ...prev, ...patch }))}
        />
      );
    }

    if (activeTab === "newApplications") {
      return (
        <NewApplicationsWithRoleTabs
          locale={locale}
          rolesLower={rolesLower}
          departmentId={profile?.department?.id}
        />
      );
    }

    if (activeTab === "criteriaSelection") {
      if (isExternalExpert) {
        return (
          <div className="home-section">
            <h2>⚠️ {locale === "kz" ? "Қолжетімсіз" : "Недоступно"}</h2>
            <p>
              {locale === "kz" ? "Бұл бөлім сіз үшін қолжетімді емес." : "Этот раздел вам недоступен."}
            </p>
          </div>
        );
      }
      return (
        <section className="dashboard-body">
          <h2>🧮 {locale === "kz" ? "Критерий таңдау" : "Выбор критериев"}</h2>
          <SelectCriteriaInternalExpert locale={locale} />
        </section>
      );
    }

    if (activeTab === "inExpertise") {
      return (
        <section className="dashboard-body">
          <h2>📁 {locale === "kz" ? "Сараптамадағы ОӘК" : "УМК на экспертизе"}</h2>
          {isInternalExpert ? (
            <InExpertiseApplicationsInternalExpert locale={locale} />
          ) : isExternalExpert ? (
            <ExternalExpertApplicationsList locale={locale} />
          ) : isDeputy2 ? (
            <InExpertiseApplicationsZam2 locale={locale} />
          ) : (
            <InExpertiseApplicationsList locale={locale} />
          )}
        </section>
      );
    }

    if (activeTab === "documents") return <DocumentsPage locale={locale} />;

    // FIX: защита — без прав показываем сообщение вместо null
    if (activeTab === "users") {
      if (!canOpenUsers) {
        return (
          <div className="home-section">
            <h2>⚠️ {locale === "kz" ? "Қолжетімсіз" : "Недоступно"}</h2>
            <p>{locale === "kz" ? "Бұл бөлім сіз үшін қолжетімді емес." : "Этот раздел вам недоступен."}</p>
          </div>
        );
      }
      return <UsersPage locale={locale} />;
    }

    if (activeTab === "summary") return <SummaryPage locale={locale} />;

    return null;
  };

  if (!authReady) {
    return (
      <div style={{ padding: 24, fontSize: 18 }}>
        {authError || (locale === "kz" ? "Кіру тексерілуде..." : "Проверка входа...")}
      </div>
    );
  }

  // FIX: showInExpertiseTab теперь использует ID отдела, а не хрупкую строку названия
const showInExpertiseTab =
  !isModerator &&
  !isRegistrar &&
  !isAccountant &&
  !isDeputy2 &&
  !isDepartmentHead && 
  !isInternalExpert &&  // ← добавить
  profile?.department?.id !== DEPT_ID_HIDE_IN_EXPERTISE;

  return (
    <div className="staff-dashboard">
      <aside className="sd-sidebar" role="navigation" aria-label="Staff sidebar">
        <h2 className="sd-title">EXPERTISE</h2>

        <ul className="sd-menu">
          <li
            className={`sd-item ${activeTab === "home" ? "is-active" : ""}`}
            onClick={() => handleTabChange("home")}
          >
            <FaHome style={{ marginRight: 8 }} />
            {locale === "kz" ? "Басты бет" : "Главная"}
          </li>

          {hasRole("moderator") && (
            <li
              className={`sd-item ${activeTab === "moderatorCriteriasHub" ? "is-active" : ""}`}
              onClick={() => handleTabChange("moderatorCriteriasHub")}
            >
              🧩 {locale === "kz" ? "Критерийлер" : "Критерии"}
            </li>
          )}

          {!isModerator && !isRegistrar && !isAccountant && (
            <li
              className={`sd-item ${activeTab === "newApplications" ? "is-active" : ""}`}
              onClick={() => handleTabChange("newApplications")}
            >
              {isDeputy2 || isDepartmentHead || isInternalExpert ? (
                <>📁 {locale === "kz" ? "Сараптамада" : "На экспертизе"}</>
              ) : (
                <>📄 {locale === "kz" ? "Жаңа өтінімдер" : "Новые заявки"}</>
              )}
            </li>
          )}

          {/* FIX: условие переписано через ID отдела */}
          {showInExpertiseTab && (
            <li
              className={`sd-item ${activeTab === "inExpertise" ? "is-active" : ""}`}
              onClick={() => handleTabChange("inExpertise")}
            >
              📁 {locale === "kz" ? "Сараптамада" : "На экспертизе"}
            </li>
          )}

          {profile && (
            <>
              {(isGosService || isDirector) && (
                <li
                  className={`sd-item ${activeTab === "summary" ? "is-active" : ""}`}
                  onClick={() => handleTabChange("summary")}
                >
                  📋 {locale === "kz" ? "Қорытынды" : "Заключение"}
                </li>
              )}
            </>
          )}

          {profile && (
            <li
              className={`sd-item ${activeTab === "orders" || activeTab === "newOrder" ? "is-active" : ""}`}
              onClick={() => handleTabChange("orders")}
            >
              <FaFileSignature style={{ marginRight: 8 }} />
              {locale === "kz" ? "Бұйрықтар" : "Приказы"}
            </li>
          )}

          <li
            className={`sd-item ${activeTab === "profile" ? "is-active" : ""}`}
            onClick={() => handleTabChange("profile")}
          >
            <FaUser style={{ marginRight: 8 }} />
            {locale === "kz" ? "Профиль" : "Профиль"}
          </li>

          {canOpenUsers && (
            <li
              className={`sd-item ${activeTab === "users" ? "is-active" : ""}`}
              onClick={() => handleTabChange("users")}
            >
              <FaUsers style={{ marginRight: 8 }} />
              {locale === "kz" ? "Қолданушылар" : "Пользователи"}
            </li>
          )}

          {canOpenPublishers && (
            <li
              className={`sd-item ${activeTab === "publishers" ? "is-active" : ""}`}
              onClick={() => handleTabChange("publishers")}
            >
              🏢 {locale === "kz" ? "Издательстволар" : "Издательства"}
            </li>
          )}

          {hasRole("moderator", "admin", "administrator", "директор", "director") && (
            <li
              className={`sd-item ${activeTab === "references" ? "is-active" : ""}`}
              onClick={() => handleTabChange("references")}
            >
              📚 {locale === "kz" ? "Басқару тақтасы" : "Панель управление"}
            </li>
          )}

          <li
            className={`sd-item ${activeTab === "instruction" ? "is-active" : ""}`}
            onClick={() => handleTabChange("instruction")}
          >
            🎥 {locale === "kz" ? "Нұсқаулық" : "Инструкция"}
          </li>

          <li className="sd-item" onClick={handleLogout}>
            <FaSignOutAlt style={{ marginRight: 8 }} />
            {locale === "kz" ? "Шығу" : "Выход"}
          </li>
        </ul>

        <div className="sd-lang">
          <button
            className={`sd-lang-btn ${locale === "kz" ? "is-active" : ""}`}
            onClick={() => toggleLanguage("kz")}
            type="button"
          >
            KZ
          </button>
          <button
            className={`sd-lang-btn ${locale === "ru" ? "is-active" : ""}`}
            onClick={() => toggleLanguage("ru")}
            type="button"
          >
            RU
          </button>
        </div>

        <img src={require("../assets/qazaq.png")} alt="ою" className="sd-orn" />
      </aside>

      {/* FIX: убран key={langVersion} — дочерние компоненты не перемонтируются при смене языка */}
      <main className="sd-main">
        {activeTab === "home" && (
          <header>
            <h1>
              {locale === "kz" ? "Қош келдіңіз, " : "Добро пожаловать, "}
              {profile?.name || (locale === "kz" ? "Қызметкер" : "Сотрудник")}!
            </h1>
          </header>
        )}

        <div>{renderContent()}</div>
      </main>
    </div>
  );
}